import React from "react";
import { FormattedMessage } from "react-intl";
import { Box, CardContent, Divider, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { CardPageItem } from "../../../../custom/Page";
import Number from "../../../../custom/Number";
import { resolveErrorMessage } from "../../../../util";
import { getAgentFinancialSummary } from "../../../../actions";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    [theme.breakpoints.down("md")]: {
      justifyContent: "space-between",
    },
  },
  gridContainer: {
    display: "grid",
    gridGap: theme.spacing(2),
    gridTemplateColumns: "repeat(3, max-content)",
    justifyContent: "space-around",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, max-content)",
    },
  },
  totalBox: {
    marginBottom: theme.spacing(1),
    textAlign: "center",
    justifyContent: "center",
    alignSelf: "center",
  },
  marginDivider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const DEFAULT_VALUES = {
  agent: 0,
  split: 0,
  recommender: 0,
  managerSplit: 0,
  total: 0,
  custodialValue: 0,
};

const PaymentsTotal = ({ agent, currentUser, dateLabel, startDate, finalDate }) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [totals, setTotals] = React.useState({ ...DEFAULT_VALUES });

  const displayTotals = React.useMemo(() => (isLoading || error ? DEFAULT_VALUES : totals), [isLoading, error, totals]);

  const infoItems = React.useMemo(
    () => [
      { id: "app.commission.type.BONUS", enable: true, value: displayTotals.agent },
      { id: "app.commission.type.BONUS_MANAGER_SPLIT", enable: true, value: displayTotals.managerSplit },
      { id: "app.commission.type.BONUS_SPLIT", enable: true, value: displayTotals.split },
      { id: "app.commission.type.BONUS_RECOMMENDANT", enable: true, value: displayTotals.recommender },
      { id: "app.commission.type.BONUS_MANAGER", enable: displayTotals?.manager > 0, value: displayTotals.manager },
      { id: "app.commission.type.BONUS_LEADERSHIP_ONE_TIME", enable: displayTotals?.managerOneTime > 0, value: displayTotals.managerOneTime },
      { id: "app.commission.type.BONUS_LEADERSHIP_MONTHLY", enable: displayTotals?.bonusLeadership > 0, value: displayTotals.bonusLeadership },
    ],
    [displayTotals],
  );

  React.useEffect(() => {
    const fetchTotal = async () => {
      try {
        if (!agent) {
          return;
        }
        setIsLoading(true);
        const formattedStartDate = startDate.format("YYYY-MM-DD");
        const formattedEndDate = finalDate.format("YYYY-MM-DD");

        const totalsData = await getAgentFinancialSummary(agent.id, formattedStartDate, formattedEndDate);
        setTotals(totalsData);
      } catch (e) {
        const errorMessage = resolveErrorMessage(e);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTotal();
  }, [agent, finalDate, startDate]);

  return (
    <CardPageItem
      raised
      double
    >
      <CardContent>
        <Box className={classes.container}>
          <Typography
            align="center"
            variant="body1"
            gutterBottom
          >
            <FormattedMessage id="app.report.financial.distribution" />
          </Typography>
          <Box className={classes.gridContainer}>
            {infoItems
              .filter((i) => i.enable)
              .map((item) => (
                <Box
                  key={item.id}
                  textAlign="center"
                >
                  <Typography
                    variant="caption"
                    align="center"
                    color="textSecondary"
                  >
                    <FormattedMessage id={item.id} />
                  </Typography>
                  <Typography variant="body2">
                    <Number
                      canCopySufix={false}
                      currency="LCT"
                      hideValue={currentUser.hideValues}
                      taxResidence={agent?.taxResidence}
                      value={item.value}
                      currentUser={currentUser}
                    />
                  </Typography>
                </Box>
              ))}
          </Box>
          <Divider className={classes.marginDivider} />
          <Box
            display={"flex"}
            justifyContent={"space-around"}
          >
            <Box className={classes.totalBox}>
              <Typography
                variant="body2"
                gutterBottom
              >
                <FormattedMessage id="app.report.totalReceived.period" />
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                gutterBottom
              >
                {dateLabel}
              </Typography>

              <Typography
                variant="h6"
                color="textPrimary"
                mt={1}
              >
                <Number
                  canCopySufix={false}
                  currency="LCT"
                  hideValue={currentUser.hideValues}
                  taxResidence={agent?.taxResidence}
                  value={displayTotals.total}
                />
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </CardPageItem>
  );
};

export default PaymentsTotal;
